import * as React from 'react';
import { AlertTriangle, ChevronDown, Info, Plus, X, Zap } from 'lucide-react';
import { AreaAddSelect } from '@/components/ui/area-chip';
import { StatusChip } from '@/components/ui/status-chip';
import {
  Callout,
  CheckRow,
  DetailHead,
  FilterChip,
  ListGroupLabel,
  RecordRow,
  SegmentedControl,
  WorkspaceButton,
  WorkspaceField,
  WorkspaceInput,
  linkishClass,
  useToast,
  workspaceInputClass,
} from '@/components/ui/workspace';
import { ApiError } from '@/lib/api/client';
import {
  PAIN_DISMISS_REASONS,
  useAddPainPoint,
  useLookAgain,
  usePainPointAction,
  type PainDismissReason,
  type PainPoint,
  type QuotedEvidence,
  type Severity,
} from '@/lib/api/discovery-findings';
import { newIdempotencyKey } from '@/lib/api/engagement-requirements';
import { cx } from '@/lib/utils/cn';
import { formatDay, plural } from '../discovery-logic';
import { DetailLabel } from './area-panes';
import { SaidRow, nameBesideCitation } from './evidence-bits';
import {
  PAIN_DISMISS_LABEL,
  SEVERITY_LABEL,
  SEVERITY_OPTIONS,
  SEVERITY_TONE,
  dismissReasonOf,
  dismissReasonText,
  manualPainProblem,
} from './findings-logic';
import type { StepRow } from './process-steps';

/**
 * Pain points of one area (E12-F07; Decisions 43, 53.14, 54.20, 54.23,
 * 54.24; the area page of 5 Nov 2026): a list on the left, the quotes a
 * pain point rests on beside it. AI proposes them from confirmed evidence
 * ("Look again" asks now); a consultant picks a severity on the row, which
 * confirms the proposal and numbers it; a confirmed one is dismissed with a
 * reason and can be restored with its number and severity. A pain point
 * whose evidence was all withdrawn is flagged, and other confirmed evidence
 * can be linked to it. Prototype: `painRow` and the `pain` and `pain-ev`
 * forms in `discovery.js`.
 *
 * A pain point with no active area is listed by `UntaggedPainPoints`, on
 * Findings under "Not tagged to an area", one line each opened in place,
 * with the same body and actions plus "+ Area" to tag it.
 */

const messageOf = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);

const toggled = (set: ReadonlySet<string>, id: string) => {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
};

/** The pain point the detail opens on: the first one waiting for a decision, else the first. */
export function defaultPainPointId(painPoints: ReadonlyArray<PainPoint>): string | null {
  const active = painPoints.filter((p) => p.status !== 'dismissed');
  return (active.find((p) => p.status === 'proposed') ?? active[0])?.id ?? null;
}

/**
 * The list: one row per pain point that is not dismissed, its severity on
 * the row. Picking a severity confirms a proposal (it then gets its
 * number); on a confirmed one it changes the severity. A proposal whose
 * evidence was all withdrawn cannot be confirmed, so the pick only sets the
 * severity until evidence is linked.
 */
export function PainPointList({
  engagementId,
  areaLabel,
  painPoints,
  selectedId,
  adding,
  onSelect,
  onAdd,
  onLookAgainStarted,
}: {
  engagementId: string;
  areaLabel: string;
  painPoints: ReadonlyArray<PainPoint>;
  selectedId: string | null;
  /** The add-by-hand form is open in the detail. */
  adding: boolean;
  onSelect: (id: string) => void;
  onAdd: () => void;
  /** AI is reading: the page asks again for a while to show new proposals. */
  onLookAgainStarted?: () => void;
}) {
  const { toast } = useToast();
  const look = useLookAgain(engagementId);
  const { restore, pending: restoring } = useRestorePainPoint(engagementId);
  const [showDismissed, setShowDismissed] = React.useState(false);
  const active = painPoints.filter((p) => p.status !== 'dismissed');
  const dismissed = painPoints.filter((p) => p.status === 'dismissed');

  const lookAgain = () =>
    look.mutate(undefined, {
      onSuccess: () => {
        onLookAgainStarted?.();
        toast({
          message: `Looking again at the confirmed evidence in ${areaLabel}. New pain points appear here as proposals; nothing counts until you confirm it.`,
        });
      },
      onError: (e) =>
        toast({ message: messageOf(e, 'Could not start Look again.'), tone: 'critical' }),
    });

  return (
    <div className="px-4 pb-5">
      <ListGroupLabel count={active.length}>Pain points</ListGroupLabel>
      <div className="mb-2 flex flex-wrap gap-4 px-1.5">
        <button
          type="button"
          className={linkishClass()}
          onClick={lookAgain}
          disabled={look.isPending}
          title="AI reads the confirmed evidence that changed since it last looked"
        >
          <Zap className="h-3 w-3" aria-hidden="true" /> Look again
        </button>
        {!adding ? (
          <button type="button" className={linkishClass()} onClick={onAdd}>
            <Plus className="h-3 w-3" aria-hidden="true" /> Add a pain point
          </button>
        ) : null}
      </div>
      {active.length ? (
        <div className="overflow-hidden rounded-card border border-ink-100 bg-paper-0">
          {active.map((p) => (
            <PainListItem
              key={p.id}
              engagementId={engagementId}
              p={p}
              selected={!adding && p.id === selectedId}
              onSelect={() => onSelect(p.id)}
            />
          ))}
        </div>
      ) : (
        <p className="px-1.5 text-meta text-ink-500">
          None yet. AI proposes pain points from confirmed evidence; you confirm them.
        </p>
      )}
      {active.some((p) => p.status === 'proposed') ? (
        <p className="mt-2 px-1.5 text-meta text-ink-500">
          Pick a severity to confirm a proposal. It then gets its number.
        </p>
      ) : null}
      {dismissed.length ? (
        <div className="mt-2 px-1.5">
          <button
            type="button"
            className={linkishClass()}
            aria-expanded={showDismissed}
            onClick={() => setShowDismissed((v) => !v)}
          >
            {showDismissed ? 'Hide dismissed' : `Dismissed (${dismissed.length})`}
          </button>
          {showDismissed ? (
            <div role="list" aria-label="Dismissed pain points" className="mt-1.5">
              {dismissed.map((p) => (
                <div role="listitem" key={p.id}>
                  <RecordRow muted icon={<X className="h-3.5 w-3.5" />}>
                    <span className="flex-none font-mono text-meta font-medium text-ink-700">
                      {p.number ?? 'Proposed'}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{p.text}</span>
                    <span className="flex-none text-meta">
                      {p.dismissed_from === 'confirmed' && p.dismiss_reason
                        ? `${p.dismiss_reason}${p.dismissed_at ? ` · ${formatDay(p.dismissed_at)}` : ''}`
                        : 'Dismissed before it was confirmed'}
                    </span>
                    <button
                      type="button"
                      className={linkishClass()}
                      onClick={() => restore(p)}
                      disabled={restoring}
                    >
                      Restore
                    </button>
                  </RecordRow>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function PainListItem({
  engagementId,
  p,
  selected,
  onSelect,
}: {
  engagementId: string;
  p: PainPoint;
  selected: boolean;
  onSelect: () => void;
}) {
  const { toast } = useToast();
  const act = usePainPointAction(engagementId);
  const { restore } = useRestorePainPoint(engagementId);
  const proposed = p.status === 'proposed';
  const fail = (fallback: string) => (e: unknown) =>
    toast({ message: messageOf(e, fallback), tone: 'critical' });

  const pick = (severity: Severity) => {
    if (proposed && !p.needs_attention) {
      act.mutate(
        { kind: 'confirm', id: p.id, severity },
        {
          onSuccess: (r) => {
            const n = (r as { number?: string } | undefined)?.number;
            toast({
              message: `${n ?? 'The pain point'} confirmed with ${SEVERITY_LABEL[severity].toLowerCase()} severity.`,
            });
          },
          onError: fail('Could not confirm it.'),
        },
      );
    } else {
      act.mutate(
        { kind: 'severity', id: p.id, severity },
        { onError: fail('Could not set the severity.') },
      );
    }
  };
  const dismiss = () =>
    act.mutate(
      { kind: 'dismiss', id: p.id, reason: null },
      {
        onSuccess: () =>
          toast({
            message: 'Dismissed. It stays on record and can be restored.',
            actions: [{ label: 'Restore', onClick: () => restore(p) }],
          }),
        onError: fail('Could not dismiss it.'),
      },
    );

  return (
    <div
      className={cx(
        'flex items-start gap-3 border-b border-l-[3px] border-b-ink-100 px-3.5 py-2.5 last:border-b-0',
        selected
          ? 'border-l-ink-900 bg-paper-50'
          : proposed
            ? 'border-l-ai-surface-border'
            : 'border-l-transparent',
      )}
    >
      <button
        type="button"
        aria-current={selected ? 'true' : undefined}
        onClick={onSelect}
        className="min-w-0 flex-1 rounded-chip text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
      >
        <span className="block text-body-sm text-ink-900">
          {p.number ? (
            <span className="mr-1.5 font-mono text-meta font-medium text-ink-700">{p.number}</span>
          ) : null}
          {p.text}
        </span>
        <span className="mt-1 flex flex-wrap items-center gap-1.5 text-meta text-ink-500">
          {p.number ? null : (
            <StatusChip tone="ai" size="sm">
              Proposed by AI
            </StatusChip>
          )}
          {p.needs_attention ? (
            <StatusChip tone="at-risk" size="sm" title="No confirmed evidence left">
              Needs evidence
            </StatusChip>
          ) : null}
          {p.origin === 'manual' ? (
            <StatusChip tone="neutral" size="sm">
              Added by hand
            </StatusChip>
          ) : null}
          <span>{plural(p.sources_count, 'source')}</span>
        </span>
      </button>
      <div className="flex flex-none flex-col items-end gap-1">
        <SegmentedControl
          size="sm"
          aria-label={`Severity of ${p.number ?? 'this pain point'}`}
          options={SEVERITY_OPTIONS}
          value={p.severity}
          readOnly={act.isPending}
          onChange={pick}
        />
        {proposed ? (
          <span className="flex items-center gap-2.5 text-meta text-ink-500">
            {!p.severity && p.ai_suggested_severity ? (
              <span className="inline-flex items-center gap-1">
                <Zap className="h-3 w-3" aria-hidden="true" /> AI suggests{' '}
                {SEVERITY_LABEL[p.ai_suggested_severity]}
              </span>
            ) : null}
            <button
              type="button"
              className={linkishClass()}
              disabled={act.isPending}
              onClick={dismiss}
            >
              Dismiss
            </button>
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The detail: the quotes the selected pain point rests on, the step of the
 * process it sits on, and what can be done with it. With `adding`, the form
 * that adds one by hand.
 */
export function PainPointDetail({
  engagementId,
  areaId,
  areaLabel,
  p,
  confirmed,
  adding,
  steps,
  onAdded,
  onOpenStep,
}: {
  engagementId: string;
  areaId: string;
  areaLabel: string;
  p: PainPoint | null;
  /** The area's confirmed evidence, to cite when adding or linking. */
  confirmed: ReadonlyArray<QuotedEvidence>;
  adding: boolean;
  /** The steps of the process this pain point sits on. */
  steps: ReadonlyArray<StepRow>;
  /** The form closed: the id of the pain point it added, or null. */
  onAdded: (id: string | null) => void;
  onOpenStep: (key: string) => void;
}) {
  const { restore } = useRestorePainPoint(engagementId);
  if (adding) {
    return (
      <ManualPainPointForm
        engagementId={engagementId}
        areaId={areaId}
        areaLabel={areaLabel}
        confirmed={confirmed}
        onDone={onAdded}
      />
    );
  }
  if (!p) {
    return (
      <p className="text-body-sm text-ink-500">
        Select a pain point to see the quotes it rests on.
      </p>
    );
  }
  return (
    <div>
      <h3 className="text-body font-semibold text-ink-900">
        {p.number ? (
          <span className="mr-1.5 font-mono text-meta font-medium text-ink-700">{p.number}</span>
        ) : null}
        {p.text}
      </h3>
      <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-meta text-ink-500">
        <span>
          {plural(p.evidence.length, 'quote')} · {plural(p.sources_count, 'source')}
        </span>
        {steps.map((s) => (
          <button
            key={s.key}
            type="button"
            className={linkishClass()}
            onClick={() => onOpenStep(s.key)}
          >
            On step {s.no} · {s.name}
          </button>
        ))}
      </p>
      <DetailLabel>What it rests on</DetailLabel>
      <PainPointBody
        engagementId={engagementId}
        p={p}
        confirmed={confirmed}
        onRestore={() => restore(p)}
        inline
      />
    </div>
  );
}

/** Restore a dismissed pain point to what it was, and say so. */
function useRestorePainPoint(engagementId: string) {
  const { toast } = useToast();
  const act = usePainPointAction(engagementId);
  const restore = (p: PainPoint) =>
    act.mutate(
      { kind: 'restore', id: p.id },
      {
        onSuccess: () =>
          toast({
            message: p.number
              ? `${p.number} restored with its number${p.severity ? ` and ${SEVERITY_LABEL[p.severity].toLowerCase()} severity` : ''}.`
              : 'The proposed pain point is restored.',
          }),
        onError: (e) => toast({ message: messageOf(e, 'Could not restore it.'), tone: 'critical' }),
      },
    );
  return { restore, pending: act.isPending };
}

/**
 * Pain points no area lists: none of their areas is active in the pinned
 * version (an AI proposal stored without one, or areas a version move
 * retired). The sign-off checklist counts a proposed one, so it is tagged
 * with an area here (it then moves to that area), or confirmed or
 * dismissed in place. Dismissed ones are not listed.
 */
export function UntaggedPainPoints({
  engagementId,
  areas,
  painPoints,
}: {
  engagementId: string;
  /** The pinned version's active areas, to tag with. */
  areas: ReadonlyArray<{ area_id: string; label: string; name: string }>;
  /** Not dismissed. */
  painPoints: ReadonlyArray<PainPoint>;
}) {
  const { restore } = useRestorePainPoint(engagementId);
  const [open, setOpen] = React.useState<string | null>(painPoints[0]?.id ?? null);
  return (
    <div className="flex flex-col gap-3.5">
      <DetailHead
        title="Not tagged to an area"
        meta="Pain points with no area in this framework version"
      />
      <Callout tone="tip" icon={Info}>
        AI proposed them without an area, or their areas were retired when the framework version
        moved. Sign-off counts a proposed one until it is confirmed or dismissed. Tag each with an
        area to list it there, or confirm or dismiss it here.
      </Callout>
      <section aria-label="Pain points not tagged to an area">
        <ListGroupLabel count={painPoints.length}>Pain points</ListGroupLabel>
        {painPoints.length ? (
          <div className="overflow-hidden rounded-card border border-ink-100 bg-paper-0">
            {painPoints.map((p) => (
              <PainPointRow
                key={p.id}
                engagementId={engagementId}
                p={p}
                confirmed={[]}
                tagAreas={areas}
                open={open === p.id}
                onToggle={() => setOpen(open === p.id ? null : p.id)}
                onRestore={() => restore(p)}
              />
            ))}
          </div>
        ) : (
          <p className="text-meta text-ink-500">
            None left. Every pain point that is not dismissed is listed under an area.
          </p>
        )}
      </section>
    </div>
  );
}

function PainPointRow({
  engagementId,
  p,
  confirmed,
  tagAreas,
  open,
  onToggle,
  onRestore,
}: {
  engagementId: string;
  p: PainPoint;
  confirmed: ReadonlyArray<QuotedEvidence>;
  /** Listed outside any area: the areas it can be tagged with (it then moves there). */
  tagAreas?: ReadonlyArray<{ area_id: string; label: string; name: string }>;
  open: boolean;
  onToggle: () => void;
  onRestore: () => void;
}) {
  const proposed = p.status === 'proposed';
  const bodyId = `pain-${p.id}`;
  return (
    <div className={cx('border-b border-ink-100 last:border-b-0', proposed && 'bg-ai-surface')}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={onToggle}
        className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-body-sm hover:bg-paper-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-amber-600"
      >
        {p.number ? (
          <span className="flex-none font-mono text-meta font-medium text-ink-700">{p.number}</span>
        ) : (
          <StatusChip tone="ai" size="sm">
            Proposed by AI
          </StatusChip>
        )}
        <span className="min-w-0 flex-1 text-ink-900">{p.text}</span>
        {p.needs_attention ? (
          <StatusChip tone="at-risk" size="sm" title="No confirmed evidence left">
            Needs evidence
          </StatusChip>
        ) : null}
        {p.origin === 'manual' ? (
          <StatusChip tone="neutral" size="sm">
            Added by hand
          </StatusChip>
        ) : null}
        {p.severity ? (
          <StatusChip tone={SEVERITY_TONE[p.severity]} size="sm">
            {SEVERITY_LABEL[p.severity]}
          </StatusChip>
        ) : (
          <StatusChip tone="unset" size="sm">
            No severity
          </StatusChip>
        )}
        <span className="flex-none text-meta text-ink-500">
          {plural(p.sources_count, 'source')}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={cx(
            'h-3.5 w-3.5 flex-none text-ink-300 transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
      {open ? (
        <div id={bodyId} className="px-3.5 pb-3.5 pl-12 pt-1">
          <PainPointBody
            engagementId={engagementId}
            p={p}
            confirmed={confirmed}
            tagAreas={tagAreas}
            onRestore={onRestore}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * What a pain point rests on and what can be done with it: the quotes,
 * the flag when all its evidence was withdrawn, linking other evidence,
 * confirming and dismissing. Shared by the area's detail pane (`inline`:
 * the list row already carries the severity and the one-click dismissal of
 * a proposal) and by the row of the untagged page, which opens in place.
 */
function PainPointBody({
  engagementId,
  p,
  confirmed,
  tagAreas,
  onRestore,
  inline = false,
}: {
  engagementId: string;
  p: PainPoint;
  confirmed: ReadonlyArray<QuotedEvidence>;
  /** Listed outside any area: the areas it can be tagged with (it then moves there). */
  tagAreas?: ReadonlyArray<{ area_id: string; label: string; name: string }>;
  onRestore: () => void;
  inline?: boolean;
}) {
  const { toast } = useToast();
  const act = usePainPointAction(engagementId);
  const [dismissing, setDismissing] = React.useState(false);
  const [choice, setChoice] = React.useState<PainDismissReason | null>(null);
  const [other, setOther] = React.useState('');
  const [linking, setLinking] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const proposed = p.status === 'proposed';
  const reason = dismissReasonOf(choice, other);

  const run = (
    a: Parameters<typeof act.mutate>[0],
    done: (r: unknown) => void,
    fallback: string,
  ) => {
    setError(null);
    act.mutate(a, { onSuccess: done, onError: (e) => setError(messageOf(e, fallback)) });
  };

  const confirm = () => {
    if (!p.severity) return;
    run(
      { kind: 'confirm', id: p.id, severity: p.severity },
      (r) => {
        const n = (r as { number?: string } | undefined)?.number;
        toast({ message: `${n ?? 'The pain point'} confirmed.` });
      },
      'Could not confirm it.',
    );
  };

  const dismiss = () => {
    if (!proposed && !reason) return;
    run(
      { kind: 'dismiss', id: p.id, reason: proposed ? null : reason },
      () => {
        setDismissing(false);
        toast({
          message:
            proposed || !reason
              ? 'Dismissed. It stays on record and can be restored.'
              : `${p.number} dismissed: ${dismissReasonText(reason)}. It keeps its number and severity, and can be restored.`,
          actions: [{ label: 'Restore', onClick: onRestore }],
        });
      },
      'Could not dismiss it.',
    );
  };

  return (
    <>
      {inline ? null : (
        <div className="mb-2 flex flex-wrap items-center gap-2.5">
          <span className="text-meta text-ink-500">Severity</span>
          <SegmentedControl
            size="sm"
            aria-label={`Severity of ${p.number ?? 'this pain point'}`}
            options={SEVERITY_OPTIONS}
            value={p.severity}
            readOnly={act.isPending}
            onChange={(severity: Severity) =>
              run({ kind: 'severity', id: p.id, severity }, () => {}, 'Could not set the severity.')
            }
          />
          {!p.severity && p.ai_suggested_severity ? (
            <span className="inline-flex items-center gap-1 text-meta text-ink-500">
              <Zap className="h-3 w-3" aria-hidden="true" /> AI suggests{' '}
              {SEVERITY_LABEL[p.ai_suggested_severity]}
            </span>
          ) : null}
        </div>
      )}
      {tagAreas ? (
        <div className="mb-2 flex flex-wrap items-center gap-2.5">
          <span className="text-meta text-ink-500">Not tagged to an area</span>
          {act.isPending ? null : (
            <AreaAddSelect
              options={tagAreas.map((a) => ({ id: a.area_id, label: a.label }))}
              onAdd={(areaId) => {
                const label = tagAreas.find((a) => a.area_id === areaId)?.label ?? 'that area';
                run(
                  { kind: 'areas', id: p.id, areaIds: [areaId] },
                  () =>
                    toast({
                      message: `${p.number ?? 'The pain point'} is now listed under ${label}.`,
                    }),
                  'Could not tag it.',
                );
              }}
            />
          )}
        </div>
      ) : null}
      {p.needs_attention ? (
        <Callout
          tone="warn"
          icon={AlertTriangle}
          className="mb-2"
          actions={
            linking || tagAreas ? undefined : (
              <button type="button" className={linkishClass()} onClick={() => setLinking(true)}>
                Link evidence
              </button>
            )
          }
        >
          {tagAreas
            ? 'No confirmed evidence left: what it cited was withdrawn. Tag it with an area to link other evidence there, or dismiss it.'
            : 'No confirmed evidence left: what it cited was withdrawn. Link other evidence, dismiss it, or leave it.'}
        </Callout>
      ) : null}
      {linking ? (
        <LinkEvidenceForm
          engagementId={engagementId}
          p={p}
          confirmed={confirmed}
          onDone={() => setLinking(false)}
        />
      ) : null}
      {p.evidence.map((e) => (
        <SaidRow key={e.evidence_id} item={e} compact />
      ))}
      {p.origin === 'manual' ? (
        <p className="mt-1.5 text-meta text-ink-500">Added by hand on {formatDay(p.created_at)}.</p>
      ) : null}
      {error ? (
        <p role="alert" className="mt-2 text-meta text-posture-critical">
          {error}
        </p>
      ) : null}
      {proposed && inline && !p.severity ? null : proposed ? (
        <div className="mt-2 flex justify-end gap-2">
          {inline ? null : (
            <WorkspaceButton variant="tertiary" disabled={act.isPending} onClick={dismiss}>
              Dismiss
            </WorkspaceButton>
          )}
          <WorkspaceButton
            variant="primary"
            disabled={!p.severity || p.needs_attention || act.isPending}
            title={
              p.needs_attention
                ? 'Link confirmed evidence first'
                : !p.severity
                  ? 'Pick a severity first'
                  : undefined
            }
            onClick={confirm}
          >
            Confirm
          </WorkspaceButton>
        </div>
      ) : dismissing ? (
        <div
          role="group"
          aria-label={`Why ${p.number} is dismissed`}
          className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-dashed border-ink-100 pt-2"
        >
          {PAIN_DISMISS_REASONS.map((r) => (
            <FilterChip key={r} on={choice === r} onClick={() => setChoice(r)}>
              {PAIN_DISMISS_LABEL[r]}
            </FilterChip>
          ))}
          {choice === 'other' ? (
            <WorkspaceInput
              autoFocus
              aria-label="Reason in a few words"
              placeholder="A few words"
              className="h-7 max-w-[260px]"
              value={other}
              onChange={(e) => setOther(e.target.value)}
            />
          ) : null}
          <span className="flex-1" />
          <button type="button" className={linkishClass()} onClick={() => setDismissing(false)}>
            Cancel
          </button>
          <WorkspaceButton
            disabled={!reason || act.isPending}
            title={reason ? undefined : 'Pick a reason first'}
            onClick={dismiss}
          >
            Dismiss {p.number}
          </WorkspaceButton>
        </div>
      ) : (
        <div className="mt-2 flex justify-end">
          <button type="button" className={linkishClass()} onClick={() => setDismissing(true)}>
            Dismiss…
          </button>
        </div>
      )}
    </>
  );
}

/** The area's confirmed evidence to pick from (a pain point always rests on at least one). */
function EvidencePicker({
  items,
  picked,
  onToggle,
  label,
}: {
  items: ReadonlyArray<QuotedEvidence>;
  picked: ReadonlySet<string>;
  onToggle: (id: string) => void;
  label: string;
}) {
  if (items.length === 0) {
    return (
      <p className="text-meta text-ink-500">This area has no confirmed evidence to cite yet.</p>
    );
  }
  return (
    <div
      role="group"
      aria-label={label}
      className="max-h-[240px] overflow-y-auto rounded-card border border-ink-100 bg-paper-0 px-2.5"
    >
      {items.map((e) => {
        const who = nameBesideCitation(e.citation, e.source);
        return (
          <CheckRow
            key={e.evidence_id}
            checked={picked.has(e.evidence_id)}
            onToggle={() => onToggle(e.evidence_id)}
          >
            <span className="block">{e.text}</span>
            <span className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className="font-mono text-meta font-medium text-ink-700">{e.number}</span>
              <span className="font-mono text-[11px] text-ink-500">
                {[e.citation.label, e.citation.location].filter(Boolean).join(' · ')}
              </span>
              {who ? <span className="text-meta text-ink-500">{who}</span> : null}
            </span>
          </CheckRow>
        );
      })}
    </div>
  );
}

/** Link more confirmed evidence to a pain point (Decision 54.20). */
function LinkEvidenceForm({
  engagementId,
  p,
  confirmed,
  onDone,
}: {
  engagementId: string;
  p: PainPoint;
  confirmed: ReadonlyArray<QuotedEvidence>;
  onDone: () => void;
}) {
  const { toast } = useToast();
  const act = usePainPointAction(engagementId);
  const [picked, setPicked] = React.useState<Set<string>>(() => new Set());
  const [error, setError] = React.useState<string | null>(null);
  const linked = new Set(p.evidence.map((e) => e.evidence_id));
  const items = confirmed.filter((e) => !linked.has(e.evidence_id));
  return (
    <Callout tone="form" className="mb-2">
      <p className="mb-2 text-body-sm font-semibold text-ink-900">
        Link confirmed evidence to {p.number ?? 'this pain point'}
      </p>
      <EvidencePicker
        items={items}
        picked={picked}
        label="Evidence to link"
        onToggle={(id) => setPicked((s) => toggled(s, id))}
      />
      {error ? (
        <p role="alert" className="mt-2 text-meta text-posture-critical">
          {error}
        </p>
      ) : null}
      <div className="mt-2.5 flex justify-end gap-2">
        <WorkspaceButton variant="tertiary" onClick={onDone}>
          Cancel
        </WorkspaceButton>
        <WorkspaceButton
          variant="primary"
          disabled={picked.size === 0 || act.isPending}
          onClick={() => {
            setError(null);
            act.mutate(
              { kind: 'link', id: p.id, evidenceIds: [...picked] },
              {
                onSuccess: () => {
                  toast({
                    message: `Linked ${plural(picked.size, 'evidence item')} to ${p.number ?? 'the pain point'}.`,
                  });
                  onDone();
                },
                onError: (e) => setError(messageOf(e, 'Could not link the evidence.')),
              },
            );
          }}
        >
          {picked.size ? `Link ${picked.size}` : 'Link'}
        </WorkspaceButton>
      </div>
    </Callout>
  );
}

/** Add a pain point by hand: it rests on confirmed evidence and is numbered at once (54.24). */
function ManualPainPointForm({
  engagementId,
  areaId,
  areaLabel,
  confirmed,
  onDone,
}: {
  engagementId: string;
  areaId: string;
  areaLabel: string;
  confirmed: ReadonlyArray<QuotedEvidence>;
  onDone: (id: string | null) => void;
}) {
  const { toast } = useToast();
  const add = useAddPainPoint(engagementId);
  const [text, setText] = React.useState('');
  const [picked, setPicked] = React.useState<Set<string>>(() => new Set());
  const [severity, setSeverity] = React.useState<Severity | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const problem = manualPainProblem(text, picked.size, severity);

  const submit = () => {
    if (problem || !severity) return;
    setError(null);
    add.mutate(
      {
        body: { text: text.trim(), area_ids: [areaId], evidence_ids: [...picked], severity },
        key: newIdempotencyKey(),
      },
      {
        onSuccess: (r) => {
          toast({
            message: `${r.number ?? 'The pain point'} added by hand, citing ${plural(picked.size, 'evidence item')}.`,
          });
          onDone(r.id);
        },
        onError: (e) => setError(messageOf(e, 'Could not add the pain point.')),
      },
    );
  };

  return (
    <section aria-label="Add a pain point" className="mb-2.5">
      <Callout tone="form">
        <h3 className="text-body-sm font-semibold text-ink-900">Add a pain point in {areaLabel}</h3>
        <p className="mb-2.5 text-meta text-ink-500">
          It must rest on confirmed evidence, and you set its severity. It gets the next number when
          you add it.
        </p>
        <div className="flex flex-col gap-3">
          <WorkspaceField label="Pain point">
            {(id) => (
              <textarea
                id={id}
                rows={2}
                placeholder="What hurts, in one sentence"
                className={cx(workspaceInputClass, 'h-auto py-2')}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            )}
          </WorkspaceField>
          <div>
            <p className="mb-1 text-meta font-medium text-ink-700">
              Evidence it rests on{' '}
              <span className="font-normal text-ink-500">· {picked.size} picked</span>
            </p>
            <EvidencePicker
              items={confirmed}
              picked={picked}
              label="Evidence it rests on"
              onToggle={(id) => setPicked((s) => toggled(s, id))}
            />
          </div>
          {error ? (
            <p role="alert" className="text-meta text-posture-critical">
              {error}
            </p>
          ) : null}
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2.5">
          <span className="text-meta text-ink-500">Severity</span>
          <SegmentedControl
            aria-label="Severity"
            size="sm"
            options={SEVERITY_OPTIONS}
            value={severity}
            onChange={setSeverity}
          />
          <span className="flex-1" />
          {problem ? <span className="text-meta text-ink-500">{problem}.</span> : null}
          <WorkspaceButton variant="tertiary" size="md" onClick={() => onDone(null)}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton
            variant="primary"
            size="md"
            disabled={Boolean(problem) || add.isPending}
            onClick={submit}
          >
            {add.isPending ? 'Adding…' : 'Add pain point'}
          </WorkspaceButton>
        </div>
      </Callout>
    </section>
  );
}
